const PictProvider = require('pict-provider');

/** @typedef {import('../HeadlightConfigurableApplication.js').Pict} Pict */

/** Ships on by default; a manifest opts out with `AutoSave: false` (or `AutoSave: { Enabled: false }`). */
const DEFAULT_INTERVAL_SECONDS = 90;
/** Below this, saves stack up behind each other on a slow connection and hammer the API. */
const MINIMUM_INTERVAL_SECONDS = 15;
/** An hour. Anything longer is indistinguishable from "off" and is almost certainly a typo. */
const MAXIMUM_INTERVAL_SECONDS = 3600;
/** Consecutive failed auto-saves before the provider stops trying. */
const DEFAULT_MAX_CONSECUTIVE_FAILURES = 3;

/**
 * Auto-save states, in the order a healthy session moves through them.
 * `offline` and `retrying` are transient; `suspended` is terminal until a
 * successful save (manual or programmatic) revives the provider.
 */
const STATES = {
	DISABLED: 'disabled',
	IDLE: 'idle',
	SAVING: 'saving',
	SAVED: 'saved',
	OFFLINE: 'offline',
	RETRYING: 'retrying',
	SUSPENDED: 'suspended',
};

/**
 * Presentation metadata per state. Class names (not markup) so the navbar
 * template composes the DOM — see `HeadlightPanels-NavBar-AutoSaveStatus`.
 */
const STATE_PRESENTATION = {
	[STATES.IDLE]: { IconClass: 'fa-cloud', StateClass: 'is-idle' },
	[STATES.SAVING]: { IconClass: 'fa-circle-notch fa-spin', StateClass: 'is-saving' },
	[STATES.SAVED]: { IconClass: 'fa-check', StateClass: 'is-saved' },
	[STATES.OFFLINE]: { IconClass: 'fa-plug', StateClass: 'is-offline' },
	[STATES.RETRYING]: { IconClass: 'fa-exclamation-triangle', StateClass: 'is-retrying' },
	[STATES.SUSPENDED]: { IconClass: 'fa-exclamation-triangle', StateClass: 'is-suspended' },
};

/**
 * Message fragments that mean "the server refused you", as opposed to "the server was unreachable".
 * The REST client flattens the response into `new Error('Failed with status code 403')` (or the
 * JSON-stringified error body), so there is no status code property to read — matching on the
 * message is the only signal available.
 */
const AUTHORIZATION_ERROR_PATTERNS = [ /\b401\b/, /\b403\b/, /unauthorized/i, /forbidden/i, /not authorized/i, /permission/i ];

/** The navbar Save button in the panels configuration; auto-save follows whether it is offered. */
const SAVE_BUTTON_ID = 'HeadlightPanels-NavBar-Save-Button';

/** The document form element. Every document HTML shell uses this id, and it is never re-rendered. */
const DOCUMENT_FORM_ID = 'Headlight-Document-Form';

/**
 * Hook the framework's own "a value changed" calls so a custom input provider counts as an interaction.
 *
 * The DOM listener in the provider catches typing the moment a key lands, but pict binds `onchange` and
 * many widgets never fire a native event at all — pict-section-picker portals its dropdown to `<body>`,
 * so the option click happens outside the form and the value is written programmatically. Selecting
 * from a dropdown is half the interaction case we care about, so it has to be caught here rather than
 * in the DOM.
 *
 * Patched the same way the base application already patches the metacontroller's render hooks: the
 * upstream methods notify only a provider literally named `FormPersistence`, which this platform does
 * not register.
 */
const PictViewDynamicForm = require('pict-section-form/source/views/Pict-View-DynamicForm.js');
for (const tmpMethodName of [ 'dataChanged', 'dataChangedTabular' ])
{
	const tmpOriginalMethod = PictViewDynamicForm.prototype[tmpMethodName];
	if (typeof tmpOriginalMethod !== 'function')
	{
		continue;
	}
	PictViewDynamicForm.prototype[tmpMethodName] = function(...pArguments)
	{
		// Before, not after: the first interaction is what baselines the document for change detection,
		// and this method is what marshals the edit into it. Baselining afterwards would fold the edit
		// into the baseline, and a picker selection — which fires no DOM event to catch it earlier —
		// would never read as a change and so would never be auto-saved.
		try
		{
			this.pict?.providers?.AutoSave?.noteUserInteraction?.(tmpMethodName);
		}
		catch (pError)
		{
			// Never let auto-save bookkeeping break an edit.
			this.log?.error?.(`AutoSave: error recording a form interaction: ${pError?.message || pError}`);
		}
		return tmpOriginalMethod.apply(this, pArguments);
	};
}

/**
 * Keys under `AppData.CurrentUser.Settings.HeadlightPanels` that the Settings panel writes. They follow
 * the panel's `setting-<Key>` element-id convention so `updateUserPreferences` can paint them.
 */
const USER_PREFERENCE_ENABLED_KEY = 'AutoSave';
const USER_PREFERENCE_INTERVAL_KEY = 'AutoSaveIntervalSeconds';

/**
 * Periodically persists an editable document while the user works in it.
 *
 * Registered by `HeadlightConfigurableApplication` so every configuration-driven form app
 * (headlight-dynamic-form, materials-form, and anything else extending the base) inherits the
 * behavior without wiring anything up.
 *
 * ## Manifest configuration
 *
 * Read from the top level of the form manifest, alongside `SkipSolveWhenLocked` and friends:
 *
 * ```json
 * { "AutoSave": false }                                       // opt out entirely
 * { "AutoSave": { "IntervalSeconds": 300 } }                  // every 5 minutes instead of 90 seconds
 * { "AutoSave": { "Enabled": true, "SkipWhenUnchanged": false } }
 * ```
 *
 * | Key | Default | Meaning |
 * | --- | --- | --- |
 * | `Enabled` | `true` | Master switch. `AutoSave: false` is shorthand for `{ Enabled: false }`. |
 * | `IntervalSeconds` | `90` | Delay before the first save and between saves. Clamped to 15–3600. |
 * | `SkipWhenUnchanged` | `true` | Skip a cycle when nothing has changed since the last save. |
 * | `MaxConsecutiveFailures` | `3` | Failures in a row before auto-save suspends itself. |
 *
 * ## Guarantees
 *
 * - Sleeps between saves. The timer is dormant until a change is observed, runs one interval, saves,
 *   and goes dormant again — it does not poll. "Change" means the document differs from what the last
 *   save actually sent, so a value typed and then taken back out again wakes nothing.
 * - Never runs outside a form: it requires a `MyDefaultFormManifest`, which the web applications
 *   sharing this base class (LIMS, the configuration apps, internals, data) do not have.
 * - Follows the Save button: an application or customer whose panel configuration removes or hides the
 *   navbar Save button has turned saving off, and auto-save turns off with it.
 * - Never runs on a document the user cannot edit (locked, `CanEdit` false) or on a non-interactive
 *   render pass (the PDF/print bundle).
 * - Never runs on another customer's document, so a support user spot-checking a form in edit view does
 *   not silently stamp it. Manual saves are deliberate and are left alone.
 * - Never starts a second save lifecycle: it defers to `DocumentProvider`'s single-flight latch
 *   rather than queueing behind an in-flight save, and skips entirely while an approval action runs.
 * - Never spams a server that is refusing the write. Repeated failures — or any single failure that
 *   looks like a permission change — suspend auto-save until a save succeeds again.
 * - Works offline: in the iOS WKWebView offline template the save routes through the native document
 *   bridge instead of the REST client, and a browser reporting `navigator.onLine === false` skips the
 *   cycle without burning a failure.
 */
class AutoSaveProvider extends PictProvider
{
	/**
	 * Normalize whatever the manifest carries at `AutoSave` into a complete configuration object.
	 *
	 * Fails open — a malformed value logs and falls back to the default rather than disabling
	 * auto-save, because a typo in an optional tuning key should not silently cost a customer their
	 * unsaved work.
	 *
	 * @param {boolean | Object | undefined | null} pManifestAutoSave - The manifest's `AutoSave` value.
	 * @param {any} [pLog] - Optional logger for surfacing coercions.
	 * @return {{ Enabled: boolean, IntervalSeconds: number, SkipWhenUnchanged: boolean, MaxConsecutiveFailures: number }}
	 */
	static resolveConfiguration(pManifestAutoSave, pLog)
	{
		const tmpConfiguration =
		{
			Enabled: true,
			IntervalSeconds: DEFAULT_INTERVAL_SECONDS,
			SkipWhenUnchanged: true,
			MaxConsecutiveFailures: DEFAULT_MAX_CONSECUTIVE_FAILURES,
		};

		if (pManifestAutoSave === undefined || pManifestAutoSave === null)
		{
			return tmpConfiguration;
		}
		if (typeof pManifestAutoSave === 'boolean')
		{
			tmpConfiguration.Enabled = pManifestAutoSave;
			return tmpConfiguration;
		}
		if (typeof pManifestAutoSave !== 'object' || Array.isArray(pManifestAutoSave))
		{
			pLog?.warn?.(`AutoSave: manifest value is a ${Array.isArray(pManifestAutoSave) ? 'array' : typeof pManifestAutoSave}; expected a boolean or an object. Using auto-save defaults.`);
			return tmpConfiguration;
		}

		if ('Enabled' in pManifestAutoSave)
		{
			tmpConfiguration.Enabled = pManifestAutoSave.Enabled !== false;
		}
		if ('SkipWhenUnchanged' in pManifestAutoSave)
		{
			tmpConfiguration.SkipWhenUnchanged = pManifestAutoSave.SkipWhenUnchanged !== false;
		}
		if ('IntervalSeconds' in pManifestAutoSave)
		{
			tmpConfiguration.IntervalSeconds = AutoSaveProvider.resolveIntervalSeconds(
				pManifestAutoSave.IntervalSeconds, DEFAULT_INTERVAL_SECONDS, 'manifest IntervalSeconds', pLog);
		}
		if ('MaxConsecutiveFailures' in pManifestAutoSave)
		{
			const tmpFailures = Number(pManifestAutoSave.MaxConsecutiveFailures);
			if (!Number.isFinite(tmpFailures) || tmpFailures < 1)
			{
				pLog?.warn?.(`AutoSave: MaxConsecutiveFailures "${pManifestAutoSave.MaxConsecutiveFailures}" is not a positive number; using ${DEFAULT_MAX_CONSECUTIVE_FAILURES}.`);
			}
			else
			{
				tmpConfiguration.MaxConsecutiveFailures = Math.round(tmpFailures);
			}
		}

		return tmpConfiguration;
	}

	/**
	 * Validate and clamp an interval to the supported range.
	 *
	 * @param {any} pValue - The candidate interval, from a manifest or a user preference.
	 * @param {number} pFallback - The interval to keep when the candidate is unusable.
	 * @param {String} pSource - Where the value came from, for the warning text.
	 * @param {any} [pLog] - Optional logger.
	 * @return {number}
	 */
	static resolveIntervalSeconds(pValue, pFallback, pSource, pLog)
	{
		const tmpInterval = Number(pValue);
		if (!Number.isFinite(tmpInterval) || tmpInterval <= 0)
		{
			pLog?.warn?.(`AutoSave: ${pSource} interval "${pValue}" is not a positive number; using ${pFallback} seconds.`);
			return pFallback;
		}
		const tmpRounded = Math.round(tmpInterval);
		const tmpClamped = Math.min(MAXIMUM_INTERVAL_SECONDS, Math.max(MINIMUM_INTERVAL_SECONDS, tmpRounded));
		if (tmpClamped !== tmpRounded)
		{
			pLog?.warn?.(`AutoSave: ${pSource} interval ${tmpRounded} is outside the supported ${MINIMUM_INTERVAL_SECONDS}-${MAXIMUM_INTERVAL_SECONDS} second range; using ${tmpClamped} seconds.`);
		}
		return tmpClamped;
	}

	/**
	 * Resolve whether auto-save is on, across the three things that get a say.
	 *
	 * The precedence is deliberate and not symmetric:
	 *   - A manifest opt-out is the customer's decision about the form and nobody overrides it.
	 *   - An explicit user preference beats everything below it, in either direction.
	 *   - Impersonation only moves the *default*. Someone impersonating is testing, and a timer save
	 *     would be an edit they did not mean to make — but a tester chasing an auto-save bug has to be
	 *     able to switch it back on, so this is a preference, not a lock.
	 *
	 * @param {boolean} pManifestEnabled - Whether the form manifest allows auto-save at all.
	 * @param {boolean} pImpersonating - Whether this session is impersonating another user.
	 * @param {boolean | undefined} pUserPreference - The user's stored `AutoSave` preference, if any.
	 * @return {boolean}
	 */
	static resolveEnabled(pManifestEnabled, pImpersonating, pUserPreference)
	{
		if (!pManifestEnabled)
		{
			return false;
		}
		if (pUserPreference === false)
		{
			return false;
		}
		if (pUserPreference === true)
		{
			return true;
		}
		return !pImpersonating;
	}

	/**
	 * Layer the logged-in user's Settings-panel preferences over the manifest configuration.
	 *
	 * The user has the final say on cadence, and can switch auto-save off for themselves entirely. They
	 * cannot switch it back **on** for a form whose manifest opted out: that is a decision the customer
	 * made about the form, not a personal preference, so `Enabled` is only ever narrowed here.
	 *
	 * @param {{ Enabled: boolean, IntervalSeconds: number, SkipWhenUnchanged: boolean, MaxConsecutiveFailures: number }} pConfiguration - The manifest-resolved configuration.
	 * @param {Object | null | undefined} pUserPreferences - `CurrentUser.Settings.HeadlightPanels`.
	 * @param {any} [pLog] - Optional logger.
	 * @param {boolean} [pImpersonating] - Whether this session is impersonating another user.
	 * @return {{ Enabled: boolean, IntervalSeconds: number, SkipWhenUnchanged: boolean, MaxConsecutiveFailures: number }} A new configuration; the input is not mutated.
	 */
	static applyUserPreferences(pConfiguration, pUserPreferences, pLog, pImpersonating)
	{
		const tmpConfiguration = Object.assign({ }, pConfiguration);
		const tmpUserPreferences = (pUserPreferences && typeof pUserPreferences === 'object') ? pUserPreferences : { };
		// `pConfiguration` is the manifest's configuration, so its Enabled is the ceiling.
		tmpConfiguration.Enabled = AutoSaveProvider.resolveEnabled(
			pConfiguration.Enabled, Boolean(pImpersonating), tmpUserPreferences[USER_PREFERENCE_ENABLED_KEY]);
		if (tmpUserPreferences[USER_PREFERENCE_INTERVAL_KEY] !== undefined && tmpUserPreferences[USER_PREFERENCE_INTERVAL_KEY] !== null)
		{
			tmpConfiguration.IntervalSeconds = AutoSaveProvider.resolveIntervalSeconds(
				tmpUserPreferences[USER_PREFERENCE_INTERVAL_KEY], tmpConfiguration.IntervalSeconds, 'user preference', pLog);
		}
		return tmpConfiguration;
	}

	/**
	 * @param {any} pFable - The Fable instance.
	 * @param {Object} [pOptions] - The provider options.
	 * @param {String} [pServiceHash] - The fable service hash for this instance.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		/** @type {any} */
		this.log;
		/** @type {any} */
		this.options;

		/** @type {{ Enabled: boolean, IntervalSeconds: number, SkipWhenUnchanged: boolean, MaxConsecutiveFailures: number }} */
		this.Configuration = AutoSaveProvider.resolveConfiguration(undefined);
		/**
		 * The manifest's configuration before the user's preferences are layered on. Kept so the Settings
		 * panel can distinguish "the customer turned this off for this form" from "you turned it off".
		 * @type {{ Enabled: boolean, IntervalSeconds: number, SkipWhenUnchanged: boolean, MaxConsecutiveFailures: number }}
		 */
		this.ManifestConfiguration = this.Configuration;
		/** @type {boolean} - Whether the interval timer is currently armed. */
		this.Running = false;
		/** @type {boolean} - Whether auto-save stopped itself because saving kept failing. */
		this.Suspended = false;
		/** @type {number} - Failed auto-saves since the last successful one. */
		this.ConsecutiveFailures = 0;
		/** @type {any} - The pending interval timer handle. */
		this._Timer = null;
		/** @type {?String} - Serialized document state as of the last save, for change detection. */
		this._SavedSnapshot = null;
		/** @type {boolean} - Whether the save currently in flight was started by this provider. */
		this._AutoSaveInFlight = false;
		/** @type {?Date} - When a save last completed, whoever asked for it. */
		this.LastSavedAt = null;
		/**
		 * Whether the user has modified anything in this form yet. The clock does not start until they
		 * have: opening someone's form to read it should never write to it 90 seconds later.
		 * @type {boolean}
		 */
		this.UserHasInteracted = false;
		/** @type {?Function} - The detach handle for the first-interaction DOM listener. */
		this._DetachInteractionListener = null;
		/** @type {String} */
		this.State = STATES.DISABLED;
		/** @type {String} */
		this.StateMessage = '';

		this._publishStatus();
	}

	/**
	 * Read the manifest's `AutoSave` block and arm the timer if the document qualifies.
	 *
	 * Called from the base application once the document is loaded and the form has rendered, so
	 * `Locked` / `CanEdit` / the print-vs-edit theme decision have all been made by then.
	 *
	 * @return {boolean} Whether auto-save is now running.
	 */
	initializeFromManifest()
	{
		this.ManifestConfiguration = AutoSaveProvider.resolveConfiguration(this.pict.settings?.MyDefaultFormManifest?.AutoSave, this.log);
		this.Configuration = AutoSaveProvider.applyUserPreferences(
			this.ManifestConfiguration, this.readUserPreferences(), this.log, this.isImpersonating());
		return this.start();
	}

	/**
	 * Whether this session is impersonating another user — CheckSession reports it.
	 *
	 * Read permissively: this only moves a default the user can change, so treating an unexpected
	 * truthy shape as impersonation errs toward not writing to a document somebody is testing on.
	 * @return {boolean}
	 */
	isImpersonating()
	{
		return Boolean(this.pict.AppData?.CurrentUser?.Impersonation);
	}

	/**
	 * The logged-in user's panel preferences, or undefined when nobody is logged in.
	 * @return {Object | undefined}
	 */
	readUserPreferences()
	{
		return this.pict.AppData?.CurrentUser?.Settings?.HeadlightPanels;
	}

	/**
	 * Re-read the manifest and the user's Settings-panel preferences, then re-arm.
	 *
	 * Called when the user changes an auto-save preference, so it takes effect on the open document
	 * rather than at the next page load. Re-arming also resets the countdown, which is the behavior a
	 * user changing the cadence expects.
	 * @return {boolean} Whether auto-save is running after the change.
	 */
	reconfigure()
	{
		return this.initializeFromManifest();
	}

	/**
	 * Describe auto-save for the Settings panel.
	 *
	 * `Available` is about whether the controls are meaningful on this screen at all, not about the
	 * document currently open: the preference is per-user and carries to their next document, so a
	 * locked document does not make it unavailable.
	 *
	 * @return {{ Available: boolean, Locked: boolean, Reason: string, Enabled: boolean, IntervalSeconds: number, DefaultIntervalSeconds: number, MinimumIntervalSeconds: number, MaximumIntervalSeconds: number }}
	 */
	getUserFacingState()
	{
		// Not a form application, or an application whose navbar offers no Save button: the controls
		// would be inert, so the panel hides them entirely.
		const tmpAvailable = Boolean(this.pict.settings?.MyDefaultFormManifest) && this.isSaveControlAvailable();
		// Conditions the user cannot change from here. Both leave the rows visible but inert, with the
		// reason spelled out, rather than a live toggle that does nothing.
		const tmpForeignCustomer = !this.isSameCustomerAsDocument();
		const tmpManifestOptedOut = !this.ManifestConfiguration.Enabled;
		const tmpEnabled = this.Configuration.Enabled && !tmpForeignCustomer;
		// Locked is "you cannot change this here". Impersonation is not locked — it only moved the
		// default — but it still explains itself, so the reason is tracked separately.
		let tmpReason = '';
		if (tmpForeignCustomer)
		{
			tmpReason = 'Auto-save is off because this document belongs to another customer.';
		}
		else if (tmpManifestOptedOut)
		{
			tmpReason = 'Auto-save is turned off for this form type.';
		}
		else if (!tmpEnabled && this.isImpersonating())
		{
			tmpReason = 'Auto-save defaults to off while impersonating. Turn it on if you are testing auto-save itself.';
		}
		return {
			Available: tmpAvailable,
			Locked: tmpForeignCustomer || tmpManifestOptedOut,
			Reason: tmpReason,
			Enabled: tmpEnabled,
			IntervalSeconds: this.Configuration.IntervalSeconds,
			DefaultIntervalSeconds: DEFAULT_INTERVAL_SECONDS,
			MinimumIntervalSeconds: MINIMUM_INTERVAL_SECONDS,
			MaximumIntervalSeconds: MAXIMUM_INTERVAL_SECONDS,
		};
	}

	/**
	 * Whether this document should be auto-saved at all.
	 *
	 * Re-checked on every cycle, not just at start: a document that gets locked mid-session (the user
	 * submits it in another tab, an approver locks it) must stop being written to.
	 * @return {boolean}
	 */
	isEligible()
	{
		if (!this.Configuration.Enabled)
		{
			return false;
		}
		// No form manifest means no form. Roughly a dozen web applications (LIMS, the configuration
		// apps, internals, data) extend this same base class and several of them set Interactive true
		// for their own purposes, so this — not Interactive — is what structurally keeps auto-save out
		// of them. The base class does not bootstrap form section views without a manifest either.
		if (!this.pict.settings?.MyDefaultFormManifest)
		{
			return false;
		}
		// The PDF/print render pass loads the same application with Interactive false. Saving there
		// would write the render's derived state back over the document.
		if (!this.pict.AppData?.Interactive)
		{
			return false;
		}
		const tmpReportData = this.pict.AppData?.DocumentData?.ReportData;
		if (!tmpReportData)
		{
			return false;
		}
		// A TINYINT Locked can arrive as the string "0", which is JS-truthy — the same trap
		// ManifestConditionEvaluator guards against.
		if (tmpReportData.Locked && tmpReportData.Locked !== '0')
		{
			return false;
		}
		if (!tmpReportData.CanEdit)
		{
			return false;
		}
		if (!this.isSameCustomerAsDocument())
		{
			return false;
		}
		return this.isSaveControlAvailable();
	}

	/**
	 * Whether the logged-in user belongs to the same customer as the open document.
	 *
	 * Support and engineering users (role index 5) open other customers' forms to spot-check them — open,
	 * read, close, no edits intended. In edit view a timer save would still fire, stamping the document
	 * with a foreign updating user, and in some configurations that leaves the customer's own users
	 * unable to open or edit it afterwards. A person who deliberately clicks Save has decided to write;
	 * a timer has not, so this guard is deliberately scoped to auto-save and leaves manual saves alone.
	 *
	 * Safe to apply unconditionally: only customer 1 (Headlight's own team) is supposed to be able to
	 * edit across customers at all, so a mismatch here is always an internal tester looking at someone
	 * else's document — never a customer doing their own work. That is why this gates on the customer
	 * rather than on the role index: a role-5 user inside their own customer is doing ordinary work and
	 * keeps auto-save.
	 *
	 * Fails open when either id is unresolvable: absence is not evidence of a mismatch, and the offline
	 * template has no session to compare against at all. Both ids come back as database integers, but
	 * they cross JSON boundaries from different endpoints, so compare them numerically rather than by
	 * identity.
	 * @return {boolean}
	 */
	isSameCustomerAsDocument()
	{
		const tmpUserCustomerID = Number(this.pict.AppData?.CurrentUser?.CustomerID);
		const tmpDocumentCustomerID = Number(this.pict.AppData?.DocumentData?.ReportData?.IDCustomer);
		if (!Number.isFinite(tmpUserCustomerID) || !Number.isFinite(tmpDocumentCustomerID) || tmpUserCustomerID <= 0 || tmpDocumentCustomerID <= 0)
		{
			return true;
		}
		return tmpUserCustomerID === tmpDocumentCustomerID;
	}

	/**
	 * Whether the panels navbar offers this application a Save button.
	 *
	 * Auto-save follows the Save button: an application or customer that turned saving off through the
	 * panel configuration should not have a timer writing the document behind their back. This reads
	 * the configuration rather than the DOM deliberately — the navbar hides the Save button at runtime
	 * whenever there is no document context, and that toggle is not a statement about whether saving is
	 * offered.
	 *
	 * Fails open when there is no panels configuration at all: an embedded or stripped shell that never
	 * renders a navbar has said nothing either way, and the manifest and document gates still apply.
	 * @return {boolean}
	 */
	isSaveControlAvailable()
	{
		const tmpNavBar = this.pict.settings?.HeadlightPanels?.Configuration?.NavBar;
		if (!tmpNavBar || !Array.isArray(tmpNavBar.Buttons))
		{
			return true;
		}
		if (tmpNavBar.hideNavbar)
		{
			return false;
		}
		const tmpSaveButton = tmpNavBar.Buttons.find((pButton) => pButton && (pButton.id === SAVE_BUTTON_ID || pButton.name === 'save'));
		if (!tmpSaveButton)
		{
			return false;
		}
		return !String(tmpSaveButton.wrapperClass || '').split(/\s+/).includes('is-hidden');
	}

	/**
	 * Arm the auto-save timer.
	 * @return {boolean} Whether the timer was armed.
	 */
	start()
	{
		this.stop();
		this.Suspended = false;
		this.ConsecutiveFailures = 0;
		if (!this.isEligible())
		{
			this.log.info('AutoSave: not enabled for this document (disabled by manifest, locked, read-only, or a non-interactive render).');
			this._setState(STATES.DISABLED);
			return false;
		}
		this.Running = true;
		if (!this.UserHasInteracted)
		{
			// A form opened to be read — the case a support user spot-checking someone else's document is
			// in — is never written to. The baseline moves to that first edit, which is also truer than
			// this one: entity hydration, the media registry and the load-time solve all settle in
			// between, and none of that is the user's work.
			this._captureSnapshot();
		}
		this._listenForInteractions();
		this._setState(STATES.IDLE);
		// Dormant by default. Re-arming here covers a reconfigure (the user changing the cadence in
		// Settings) that lands on top of edits which have not been written yet.
		if (this.UserHasInteracted && this._hasPendingWork())
		{
			this.log.info(`AutoSave: enabled; unsaved changes are pending, saving in ${this.Configuration.IntervalSeconds} seconds.`);
			this._scheduleNextCycle();
			return true;
		}
		this.log.info(`AutoSave: enabled; the ${this.Configuration.IntervalSeconds} second countdown starts at the next change.`);
		return true;
	}

	/**
	 * Whether there is anything worth waking up for.
	 *
	 * The document as it stands differs from what the last save actually sent — so a value typed and
	 * then taken back out again is not pending work, because the payload would be identical.
	 * `SkipWhenUnchanged: false` is a manifest opting out of exactly this reasoning, so it keeps the
	 * timer running regardless.
	 * @return {boolean}
	 */
	_hasPendingWork()
	{
		if (!this.Configuration.SkipWhenUnchanged)
		{
			return true;
		}
		return this._hasUnsavedChanges();
	}

	/**
	 * Record that the user has modified the form, and start the countdown if this is the first time.
	 *
	 * Cheap and idempotent — it is called on every keystroke and every framework data change, so the
	 * already-interacted path is a single boolean test.
	 *
	 * @param {String} [pSource] - What observed the interaction, for the log line.
	 * @return {void}
	 */
	noteUserInteraction(pSource)
	{
		const tmpFirstEver = !this.UserHasInteracted;
		if (tmpFirstEver)
		{
			this.UserHasInteracted = true;
		}
		if (!this.Running)
		{
			// Auto-save is off for this document; the flag still latches so a later reconfigure (the user
			// switching it on in Settings mid-edit) does not have to wait for another edit.
			return;
		}
		if (this._Timer)
		{
			// A countdown is already running. Deliberately not restarted — a fast typist would otherwise
			// push the deadline out indefinitely and never be saved.
			return;
		}
		if (tmpFirstEver)
		{
			// Baseline the document as it stands immediately before this first edit lands, so the edit
			// itself is what the cycle sees as a change. After this, only a save moves the baseline.
			this._captureSnapshot();
		}
		this.log.info(`AutoSave: change observed (${pSource || 'unknown'}); saving in ${this.Configuration.IntervalSeconds} seconds.`);
		this._scheduleNextCycle();
	}

	/**
	 * Watch the document form for the first sign of the user entering data.
	 *
	 * One delegated listener on the form element, in the capture phase. The per-element
	 * `addEventListener` ban does not apply: this is a single listener on the shell's own `<form>`,
	 * which is never re-rendered (the section views render inside it), so there is nothing to leak.
	 * `input` rather than `change` because pict binds `onchange` — waiting for blur would leave someone
	 * typing for minutes with no countdown running at all.
	 * @return {void}
	 */
	_listenForInteractions()
	{
		this._stopListeningForInteractions();
		const tmpFormElement = this._resolveDocumentFormElement();
		if (!tmpFormElement)
		{
			// No form element to watch. The framework hooks still catch edits; this is only the fast path.
			return;
		}
		// Stays attached for the life of the provider: the timer goes dormant after every save, so each
		// later edit is what wakes it again. The handler is a couple of boolean tests once a countdown
		// is already running, which is what makes it cheap enough to sit on every keystroke.
		const fHandler = (pEvent) =>
		{
			this.noteUserInteraction(pEvent?.type === 'input' ? 'typing' : 'selection');
		};
		tmpFormElement.addEventListener('input', fHandler, true);
		tmpFormElement.addEventListener('change', fHandler, true);
		this._DetachInteractionListener = () =>
		{
			tmpFormElement.removeEventListener('input', fHandler, true);
			tmpFormElement.removeEventListener('change', fHandler, true);
		};
	}

	/**
	 * The document form element the shells all declare, or null outside a browser.
	 * @return {?any}
	 */
	_resolveDocumentFormElement()
	{
		if (typeof document === 'undefined')
		{
			return null;
		}
		return document.getElementById(DOCUMENT_FORM_ID);
	}

	/**
	 * @return {void}
	 */
	_stopListeningForInteractions()
	{
		if (this._DetachInteractionListener)
		{
			this._DetachInteractionListener();
			this._DetachInteractionListener = null;
		}
	}

	/**
	 * Disarm the timer. Safe to call when nothing is running.
	 * @param {String} [pReason] - Logged when supplied.
	 * @return {void}
	 */
	stop(pReason)
	{
		this._stopListeningForInteractions();
		if (this._Timer)
		{
			clearTimeout(this._Timer);
			this._Timer = null;
		}
		if (this.Running && pReason)
		{
			this.log.info(`AutoSave: stopping — ${pReason}`);
		}
		this.Running = false;
	}

	/**
	 * Stop auto-saving because saving is failing, and tell the user once.
	 *
	 * Deliberately terminal: a form whose saves are being refused (expired session, revoked edit
	 * permission, a document locked out from under the user) would otherwise retry forever, filling
	 * the log and the network tab with writes that can never land.
	 *
	 * @param {String} pMessage - User-facing explanation.
	 * @return {void}
	 */
	suspend(pMessage)
	{
		this.stop();
		this.Suspended = true;
		this.log.warn(`AutoSave: suspended — ${pMessage}`);
		this._setState(STATES.SUSPENDED, pMessage);
		this.pict.views?.HeadlightPanels?.toast?.(
		{
			message: `<strong>Auto-save paused.</strong><br/><em>${pMessage}</em>`,
			type: 'is-warning',
			duration: 8000,
		});
	}

	/**
	 * Re-arm auto-save after a suspension. A successful save of any kind calls this.
	 * @return {boolean} Whether auto-save is running again.
	 */
	resume()
	{
		if (!this.isEligible())
		{
			return false;
		}
		this.log.info('AutoSave: resuming after a successful save.');
		return this.start();
	}

	/**
	 * Run one save immediately, bypassing the interval and the unchanged-document check.
	 *
	 * Resolves with the error rather than rejecting, matching `DocumentProvider.handleSaveAsync`.
	 * @return {Promise<Error|undefined>}
	 */
	async saveNow()
	{
		this._setState(STATES.SAVING);
		this._AutoSaveInFlight = true;
		// Snapshot what this save is about to send. Promoting *this* on success rather than the state
		// afterwards is what keeps an edit made while the save was in flight counted as pending — with a
		// dormant timer nothing would come back for it otherwise.
		const tmpSentSnapshot = this._serializeDocumentState();
		/** @type {Error|undefined} */
		let tmpError;
		try
		{
			tmpError = await this._executeSave();
		}
		catch (pError)
		{
			tmpError = pError;
		}
		finally
		{
			this._AutoSaveInFlight = false;
		}
		if (tmpError)
		{
			this._recordFailure(tmpError);
		}
		else
		{
			this._recordSuccess(tmpSentSnapshot);
		}
		return tmpError;
	}

	/**
	 * Notification from `DocumentProvider` that a save lifecycle finished, whoever started it.
	 *
	 * Restarts the countdown from the last save rather than from the last tick, so a manual Save
	 * resets the clock — the user who just saved should not get an auto-save two seconds later.
	 * Also revives a suspended provider: if a manual save now succeeds, whatever was refusing the
	 * write has cleared.
	 *
	 * @param {Error} [pError] - The error the save lifecycle produced, if any.
	 * @return {void}
	 */
	onSaveLifecycleSettled(pError)
	{
		// The auto-save path owns its own bookkeeping in saveNow(); this callback fires first for
		// those saves and would double-publish the status.
		if (this._AutoSaveInFlight)
		{
			return;
		}
		if (pError)
		{
			// A failed manual save is reported to the user by whoever requested it; auto-save keeps
			// its own counter for its own writes and does not escalate on someone else's failure.
			return;
		}
		this._captureSnapshot();
		this.LastSavedAt = new Date();
		if (this.Suspended)
		{
			this.resume();
			return;
		}
		if (!this.Running)
		{
			return;
		}
		this.ConsecutiveFailures = 0;
		this._setState(STATES.SAVED, '', true);
		if (!this.UserHasInteracted)
		{
			// Someone saved a form they have not edited — a deliberate manual save on a document they are
			// only looking at. That is their call to make once; it does not start a timer that would keep
			// making it for them.
			return;
		}
		// A manual save settles the document, so the timer sleeps until the next change — the same as
		// after an auto-save. Anything the user changed while the save was in flight keeps it awake.
		if (this._hasPendingWork())
		{
			this._scheduleNextCycle();
			return;
		}
		this._goDormant();
	}

	/**
	 * Put the timer to sleep until the next change wakes it.
	 *
	 * Clears any countdown still in flight: a manual save can settle part-way through one, and that
	 * pending timer would otherwise fire against a document already written.
	 * @return {void}
	 */
	_goDormant()
	{
		if (this._Timer)
		{
			clearTimeout(this._Timer);
			this._Timer = null;
		}
	}

	/**
	 * Arm the timer for the next cycle, replacing any pending one.
	 * @return {void}
	 */
	_scheduleNextCycle()
	{
		if (this._Timer)
		{
			clearTimeout(this._Timer);
		}
		this._Timer = setTimeout(() =>
		{
			this._Timer = null;
			this._runCycle();
		}, this.Configuration.IntervalSeconds * 1000);
	}

	/**
	 * One timer tick: attempt a save, then re-arm.
	 * @return {Promise<void>}
	 */
	async _runCycle()
	{
		if (!this.Running)
		{
			return;
		}
		try
		{
			await this._attemptSave();
		}
		catch (pError)
		{
			this.log.error(`AutoSave: unexpected error during an auto-save cycle: ${pError?.message || pError}`, { Stack: pError?.stack });
		}
		// A suspension inside the cycle clears Running, which is what stops the loop. Otherwise the timer
		// only continues while there is something outstanding: a save that could not run yet (a manual
		// save in flight, an approval action, no connection), or one that failed and will be retried.
		// With nothing pending it goes dormant, and the next change is what wakes it.
		if (!this.Running)
		{
			return;
		}
		if (this._hasPendingWork())
		{
			this._scheduleNextCycle();
			return;
		}
		this._goDormant();
	}

	/**
	 * Decide whether this cycle should actually write, and write if so.
	 * @return {Promise<void>}
	 */
	async _attemptSave()
	{
		if (!this.isEligible())
		{
			this.stop('the document is no longer editable.');
			this._setState(STATES.DISABLED);
			return;
		}
		const tmpDocumentProvider = this.pict.providers?.DocumentProvider;
		if (tmpDocumentProvider)
		{
			// An approval action is mid-flight (it may be locking the document right now). Writing
			// underneath it is exactly the race this feature is meant to close.
			if (tmpDocumentProvider.isTakingAction)
			{
				return;
			}
			// A manual save is already running. Its completion calls onSaveLifecycleSettled, which
			// restarts our countdown — queueing a second write here would be pure duplication.
			if (tmpDocumentProvider.IsSaving)
			{
				return;
			}
		}
		else
		{
			// No save path at all — an app shell that never registered the panels providers. Stay armed
			// in case one appears rather than reporting a failure the user cannot act on.
			return;
		}
		if (this._isBrowserOffline())
		{
			this._setState(STATES.OFFLINE, 'Waiting for a connection.');
			return;
		}
		if (this.Configuration.SkipWhenUnchanged && !this._hasUnsavedChanges())
		{
			return;
		}
		await this.saveNow();
	}

	/**
	 * Request an application save.
	 *
	 * Every save goes through the document save lifecycle, offline included — the transport is chosen
	 * inside `HeadlightDocumentService`, below this. Writing to the native bridge from here instead
	 * would skip the lifecycle, and with it the marshal and the `Document.EntityData` emission the
	 * server applies when the record reaches it.
	 *
	 * @return {Promise<Error|undefined>}
	 */
	_executeSave()
	{
		const tmpDocumentProvider = this.pict.providers?.DocumentProvider;
		if (!tmpDocumentProvider || typeof tmpDocumentProvider.handleSaveAsync !== 'function')
		{
			return Promise.resolve(new Error('No document save path is available.'));
		}
		// Muted: the whole point of auto-save is that it does not interrupt with a toast.
		return Promise.resolve(tmpDocumentProvider.handleSaveAsync(true));
	}

	/**
	 * Whether this form's saves are written to local storage rather than to the server.
	 *
	 * `HeadlightDocumentService` owns the transport decision; this only asks it, so auto-save and the
	 * save itself can never disagree about which one is running.
	 *
	 * @return {boolean}
	 */
	_isLocalDocumentTransport()
	{
		const tmpDocumentService = this.pict.services?.HeadlightDocumentService;
		if (!tmpDocumentService || typeof tmpDocumentService.isLocalDocumentTransport !== 'function')
		{
			return false;
		}
		return tmpDocumentService.isLocalDocumentTransport();
	}

	/**
	 * Whether the browser is reporting no network. Skipping on this is not a failure — the device is
	 * in a tunnel, not the server refusing us — so it must not count toward the suspension budget.
	 *
	 * A form on the local transport is never blocked by this: the save goes to the device, and having
	 * no network is the state it is built for.
	 *
	 * @return {boolean}
	 */
	_isBrowserOffline()
	{
		if (this._isLocalDocumentTransport())
		{
			return false;
		}
		return typeof navigator !== 'undefined' && navigator.onLine === false;
	}

	/**
	 * Serialize the parts of the document the save actually sends, for change detection.
	 * @return {?String} The snapshot, or null when it could not be produced.
	 */
	_serializeDocumentState()
	{
		const tmpReportData = this.pict.AppData?.DocumentData?.ReportData;
		if (!tmpReportData)
		{
			return null;
		}
		try
		{
			return JSON.stringify(
			{
				FormData: tmpReportData.FormData,
				EntityData: tmpReportData.EntityData,
				Observations: Array.isArray(tmpReportData.Observations) ? tmpReportData.Observations.map((pObservation) => pObservation?.IDObservation) : tmpReportData.Observations,
			});
		}
		catch (pError)
		{
			this.log.warn(`AutoSave: could not serialize the document for change detection (${pError?.message || pError}); auto-saving unconditionally.`);
			return null;
		}
	}

	/**
	 * Record the current document state as "saved".
	 * @return {void}
	 */
	_captureSnapshot()
	{
		this._SavedSnapshot = this._serializeDocumentState();
	}

	/**
	 * Whether anything has changed since the last snapshot. Unserializable state is treated as
	 * changed so a form with exotic values still gets saved.
	 * @return {boolean}
	 */
	_hasUnsavedChanges()
	{
		const tmpSnapshot = this._serializeDocumentState();
		if (tmpSnapshot === null || this._SavedSnapshot === null)
		{
			return true;
		}
		return tmpSnapshot !== this._SavedSnapshot;
	}

	/**
	 * @param {?String} [pSentSnapshot] - The document state this save sent, if it is known.
	 * @return {void}
	 */
	_recordSuccess(pSentSnapshot)
	{
		this.ConsecutiveFailures = 0;
		if (pSentSnapshot === undefined)
		{
			this._captureSnapshot();
		}
		else
		{
			this._SavedSnapshot = pSentSnapshot;
		}
		this.LastSavedAt = new Date();
		this._setState(STATES.SAVED, '', true);
	}

	/**
	 * Escalate a failed auto-save: back off, then suspend once it is clear the write cannot land.
	 * @param {Error} pError - The save error.
	 * @return {void}
	 */
	_recordFailure(pError)
	{
		this.ConsecutiveFailures++;
		this.log.error(`AutoSave: save attempt ${this.ConsecutiveFailures} failed: ${pError?.message || pError}`);
		if (AutoSaveProvider.isAuthorizationError(pError))
		{
			this.suspend('Your permission to edit this document has changed. Reload the page to continue.');
			return;
		}
		if (this.ConsecutiveFailures >= this.Configuration.MaxConsecutiveFailures)
		{
			this.suspend('The document could not be saved after several attempts. Use the Save button once the problem clears.');
			return;
		}
		this._setState(STATES.RETRYING, 'The last auto-save did not complete. Retrying.');
	}

	/**
	 * Whether an error means "the server refused you" rather than "the server was unreachable".
	 * @param {any} pError - The error to classify.
	 * @return {boolean}
	 */
	static isAuthorizationError(pError)
	{
		if (!pError)
		{
			return false;
		}
		const tmpStatus = pError.StatusCode ?? pError.statusCode ?? pError.status;
		if (tmpStatus === 401 || tmpStatus === 403)
		{
			return true;
		}
		const tmpMessage = String(pError.message || pError);
		return AUTHORIZATION_ERROR_PATTERNS.some((pPattern) => pPattern.test(tmpMessage));
	}

	/**
	 * @param {String} pState - One of STATES.
	 * @param {String} [pMessage] - Optional user-facing detail, surfaced as the indicator tooltip.
	 * @param {boolean} [pFlash] - Whether the indicator should briefly draw attention to itself.
	 * @return {void}
	 */
	_setState(pState, pMessage, pFlash)
	{
		this.State = pState;
		this.StateMessage = pMessage || '';
		this._publishStatus(pFlash);
	}

	/**
	 * Push the current state into AppData for the navbar indicator, and nudge the panels view to
	 * repaint it.
	 *
	 * `StatusSlot` is the one-or-zero-element array the navbar template iterates: empty renders
	 * nothing at all, which is what a form with auto-save turned off should show.
	 * @param {boolean} [pFlash] - Whether this transition should animate.
	 * @return {void}
	 */
	_publishStatus(pFlash)
	{
		const tmpRecord = this._buildStatusRecord(pFlash);
		this.pict.AppData.AutoSave =
		{
			Enabled: Boolean(this.Configuration?.Enabled),
			Running: this.Running,
			Suspended: this.Suspended,
			State: this.State,
			IntervalSeconds: this.Configuration?.IntervalSeconds,
			LastSavedISO: this.LastSavedAt ? this.LastSavedAt.toISOString() : null,
			StatusSlot: tmpRecord ? [ tmpRecord ] : [],
		};
		this.pict.views?.HeadlightPanels?.syncAutoSaveStatus?.();
	}

	/**
	 * Build the record the indicator template renders, or null when nothing should be shown.
	 * @param {boolean} [pFlash] - Whether this transition should animate.
	 * @return {?{ State: String, Label: String, IconClass: String, StateClass: String, Tooltip: String, FlashClass: String }}
	 */
	_buildStatusRecord(pFlash)
	{
		if (this.State === STATES.DISABLED)
		{
			return null;
		}
		const tmpPresentation = STATE_PRESENTATION[this.State] || STATE_PRESENTATION[STATES.IDLE];
		const tmpSavedClock = this.LastSavedAt ? this._formatClockTime(this.LastSavedAt) : '';
		let tmpLabel;
		switch (this.State)
		{
		case STATES.SAVING:
			tmpLabel = 'Saving…';
			break;
		case STATES.SAVED:
			tmpLabel = `Saved ${tmpSavedClock}`;
			break;
		case STATES.OFFLINE:
			tmpLabel = 'Waiting for connection';
			break;
		case STATES.RETRYING:
			tmpLabel = 'Save retrying';
			break;
		case STATES.SUSPENDED:
			tmpLabel = 'Auto-save paused';
			break;
		default:
			tmpLabel = tmpSavedClock ? `Saved ${tmpSavedClock}` : 'Auto-save on';
			break;
		}
		let tmpTooltip = this.StateMessage;
		if (!tmpTooltip)
		{
			tmpTooltip = tmpSavedClock ?
				`Auto-saving every ${this.Configuration.IntervalSeconds} seconds. Last saved at ${tmpSavedClock}.` :
				`Auto-saving every ${this.Configuration.IntervalSeconds} seconds.`;
		}
		return {
			State: this.State,
			Label: tmpLabel,
			IconClass: tmpPresentation.IconClass,
			StateClass: tmpPresentation.StateClass,
			Tooltip: tmpTooltip,
			FlashClass: pFlash ? 'is-flashing' : '',
		};
	}

	/**
	 * Format a wall-clock time in the document's timezone when Fable's Dates service is available.
	 * @param {Date} pDate - The moment to format.
	 * @return {String}
	 */
	_formatClockTime(pDate)
	{
		try
		{
			if (this.fable?.Dates?.dayJS)
			{
				return this.fable.Dates.dayJS(pDate).format('h:mm A');
			}
		}
		catch (pError)
		{
			this.log.trace?.(`AutoSave: falling back to the native clock formatter (${pError?.message || pError}).`);
		}
		return pDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
	}
}

module.exports = AutoSaveProvider;
module.exports.STATES = STATES;
module.exports.DEFAULT_INTERVAL_SECONDS = DEFAULT_INTERVAL_SECONDS;
module.exports.MINIMUM_INTERVAL_SECONDS = MINIMUM_INTERVAL_SECONDS;
module.exports.MAXIMUM_INTERVAL_SECONDS = MAXIMUM_INTERVAL_SECONDS;
module.exports.DEFAULT_MAX_CONSECUTIVE_FAILURES = DEFAULT_MAX_CONSECUTIVE_FAILURES;
module.exports.USER_PREFERENCE_ENABLED_KEY = USER_PREFERENCE_ENABLED_KEY;
module.exports.USER_PREFERENCE_INTERVAL_KEY = USER_PREFERENCE_INTERVAL_KEY;
